import { useMutation, useQueryClient } from '@tanstack/react-query';
import { automationStationApi } from '../api';
import { AUTOMATION_STRATEGIES } from '../helpers/constants';

function updateSpiderStrategy({ lobsangEventId, newAutomation }) {
  const url = `v1/automation/${lobsangEventId}/spider_strategy`;
  return automationStationApi.put(url, newAutomation);
}

function updateKobeStrategy({ lobsangEventId, newAutomation }) {
  const url = `v1/automation/${lobsangEventId}/kobe_strategy`;
  return automationStationApi.put(url, newAutomation);
}

function updateZoneKobeStrategy({ lobsangEventId, newAutomation }) {
  const url = `v1/automation/${lobsangEventId}/zone_kobe_strategy`;
  return automationStationApi.put(url, newAutomation);
}

const createUpdatedAutomationResponse = (newAutomation, cachedAutomation) => {
  if (newAutomation.automation_strategy === AUTOMATION_STRATEGIES.SPIDER.key) {
    // eslint-disable-next-line camelcase
    const { automation_strategy, reason, spider_exclusion, ...expectations } = newAutomation;

    return {
      ...cachedAutomation,
      current_strategy: {
        is_kobe_eligible: cachedAutomation.current_strategy?.is_kobe_eligible ?? false,
        zone_kobe_eligible: cachedAutomation.current_strategy?.zone_kobe_eligible ?? false,
        automation_strategy,
        reason,
        spider_exclusion,
        expectations,
      },
      proxy_events: {
        ...cachedAutomation.proxy_events,
      },
    };
  }

  return {
    ...cachedAutomation,
    current_strategy: {
      is_kobe_eligible: cachedAutomation.current_strategy?.is_kobe_eligible ?? false,
      zone_kobe_eligible: cachedAutomation.current_strategy?.zone_kobe_eligible ?? false,
      ...cachedAutomation.current_strategy,
      ...newAutomation,
    },
    proxy_events: {
      ...cachedAutomation.proxy_events,
    },
  };
};

export function useUpdateAutomation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({ lobsangEventId, newAutomation }) => {
      switch (newAutomation?.automation_strategy) {
        case AUTOMATION_STRATEGIES.SPIDER.key: {
          return updateSpiderStrategy({
            lobsangEventId,
            newAutomation,
          });
        }
        case AUTOMATION_STRATEGIES.KOBE.key: {
          return updateKobeStrategy({
            lobsangEventId,
            newAutomation,
          });
        }
        case AUTOMATION_STRATEGIES.KOBE_ZONE.key: {
          return updateZoneKobeStrategy({
            lobsangEventId,
            newAutomation,
          });
        }
        default: {
          throw new Error('Unknown automation strategy');
        }
      }
    },
    // Optimistic update after mutation
    onSuccess: async (data, { lobsangEventId, newAutomation }) => {
      // Retrieve the current cached response
      const cachedAutomationResponse = queryClient.getQueryData(['automation', lobsangEventId]);

      // If the cached response is not available, skip the update
      if (!cachedAutomationResponse) {
        return;
      }

      const updatedAutomationResponse = createUpdatedAutomationResponse(newAutomation, cachedAutomationResponse);

      // Update the cache with the new value
      queryClient.setQueryData(['automation', lobsangEventId], updatedAutomationResponse);

      // Refetch the data
      queryClient.invalidateQueries({ queryKey: ['automation', lobsangEventId] });
    },
  });
  return mutation;
}
